import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ConnectionService } from './connection.service';
import { SwalService } from './swal.service';

export interface Entitlements {
  baseAccess: boolean;
  proFeatures: boolean;
  team: boolean;
  trialActive: boolean;
  trialExpired: boolean;
}

export interface SubscriptionStatus {
  status: string;
  plan: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasStripeCustomer: boolean;
  checkoutEnabled: boolean;
  entitlements: Entitlements;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private connection = inject(ConnectionService);
  private swal = inject(SwalService);
  private router = inject(Router);

  /** Ultimi entitlements noti (aggiornati ad ogni getStatus). */
  private _entitlements: Entitlements | null = null;
  get entitlements(): Entitlements | null {
    return this._entitlements;
  }

  reset(): void {
    this._entitlements = null;
  }

  /** Stato abbonamento corrente (dal backend, aggiornato dai webhook Stripe). */
  getStatus(callback: (s: SubscriptionStatus) => void, error: () => void): void {
    this.connection.get(
      '@subscriptions/status',
      (s: SubscriptionStatus) => {
        this._entitlements = s?.entitlements ?? null;
        callback(s);
      },
      error,
    );
  }

  /** Carica e memorizza gli entitlements (per il gating della UI). */
  loadEntitlements(callback?: (e: Entitlements) => void): void {
    this.getStatus(
      (s) => callback?.(s.entitlements),
      () => {},
    );
  }

  canBase(): boolean {
    return !!this._entitlements?.baseAccess;
  }
  canPro(): boolean {
    return !!this._entitlements?.proFeatures;
  }
  canTeam(): boolean {
    return !!this._entitlements?.team;
  }

  /** Gate proattivo per una funzione Pro: true se consentita, altrimenti mostra il prompt. */
  requirePro(message = 'Questa funzione è disponibile con il piano Pro o Agency.'): boolean {
    if (this.canPro()) return true;
    this.promptUpgrade(message);
    return false;
  }

  /** Esegue cb assicurando prima che gli entitlements siano caricati. */
  ensureLoaded(cb: () => void): void {
    if (this._entitlements) {
      cb();
      return;
    }
    this.loadEntitlements(() => cb());
  }

  /** Dialog "abbonati" con link alla pagina Abbonamenti. */
  promptUpgrade(message = 'Passa a un piano superiore per usarla.'): void {
    this.swal
      .confirm('Funzione non disponibile', message, 'Vedi i piani', 'Chiudi')
      .then((res: any) => {
        if (res?.isConfirmed) this.router.navigate(['/abbonamenti']);
      });
  }

  /** Avvia il checkout Stripe per il piano scelto e reindirizza il browser. */
  startCheckout(plan: string, error: () => void): void {
    this.connection.post(
      'subscriptions/checkout',
      { plan },
      (res: { url: string }) => this.redirect(res),
      error,
    );
  }

  /** Registra l'interesse per un piano (beta, quando il checkout è disattivato). */
  requestPlan(
    plan: string,
    message: string,
    callback: () => void,
    error: () => void,
  ): void {
    this.connection.post(
      'subscriptions/request',
      { plan, message },
      callback,
      error,
    );
  }

  /** Disdice l'abbonamento a fine periodo (in-app, senza portale Stripe). */
  cancel(callback: () => void, error: () => void): void {
    this.connection.post('subscriptions/cancel', {}, callback, error);
  }

  /** Annulla una disdetta programmata. */
  reactivate(callback: () => void, error: () => void): void {
    this.connection.post('subscriptions/reactivate', {}, callback, error);
  }

  private redirect(res: { url: string }): void {
    if (res?.url) {
      window.location.href = res.url;
    }
  }
}
